/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ProductAvailableQuantity,
    ProductDimensionValueInventoryAvailability,
    ProductInventoryAvailability
} from '@msdyn365-commerce/retail-proxy';

/**
 * This setting defines the delivery modes supported.
 */
export enum DeliveryMode {
    allWareHouses = 0,
    shipping = 1,
    pickup = 2
}

/**
 * This setting defines the inventory levels supported.
 */
export enum InventoryLevels {
    physicalAvailable = 'physicalAvailable',
    totalAvailable = 'totalAvailable',
    threshold = 'inventoryThreshold'
}

/**
 * Product inventory information class.
 */
export interface IProductInventoryInformation {
    ProductAvailableQuantity: ProductAvailableQuantity;
    StockLevelCode?: string;
    StockLevelLabel?: string;
    IsProductAvailable: boolean;
    InventLocationId?: string;
    deliveryType?: DeliveryMode;

    /**
     * Raw Cross-LE available quantity (New-IV `AvailableQuantity`, i.e. TotalAvailable) before any
     * out-of-stock-threshold adjustment. Populated only on the New-IV (Cross-LE) path so the buybox
     * can derive the two-threshold stock label client-side without double-applying the threshold that
     * `ProductAvailableQuantity` may already reflect. Undefined on the Old-IV path.
     */
    CrossEntityAvailableQuantity?: number;

    /**
     * Raw Cross-LE ordered quantity (New-IV `OrderedSum`). Together with
     * `CrossEntityAvailableQuantity`, this reconstructs PhysicalAvailable.
     */
    CrossEntityOrderedQuantity?: number;

    /** Cross-LE designated warehouse name. */
    OrgUnitName?: string;

    /** Cross-LE designated warehouse number. */
    OrgUnitNumber?: string;
}

/**
 * This setting defines the inventory level values return by the API.
 */
export enum InventoryLevelValues {
    outOfStock = 'OOS',
    available = 'AVAIL'
}

/**
 * Retrieves inventory level code from the dimension based on the app config setting.
 * @param dimensionValuesWithInventory - The dimension info.
 * @param inventoryLevel - App config setting for inventory level.
 * @returns Inventory level code value.
 */
export const getInventoryLevelCodeFromDimensionValue = (
    dimensionValuesWithInventory: ProductDimensionValueInventoryAvailability,
    inventoryLevel?: InventoryLevels | undefined
): InventoryLevelValues | undefined => {
    if (inventoryLevel === InventoryLevels.totalAvailable) {
        return dimensionValuesWithInventory.TotalAvailableInventoryLevelCode as InventoryLevelValues | undefined;
    }
    if (inventoryLevel === InventoryLevels.physicalAvailable) {
        return dimensionValuesWithInventory.PhysicalAvailableInventoryLevelCode as InventoryLevelValues | undefined;
    }
    return undefined;
};

/**
 * Retrieves inventory level code from the product availability based on the app config setting.
 * @param productAvailability - The product availability info.
 * @param inventoryLevel - App config setting for inventory level.
 * @returns Inventory level code value.
 */
export const getInventoryLevelCodeFromProductAvailability = (
    productAvailability: ProductInventoryAvailability,
    inventoryLevel?: InventoryLevels | undefined
): InventoryLevelValues | undefined => {
    if (inventoryLevel === InventoryLevels.totalAvailable) {
        return productAvailability.TotalAvailableInventoryLevelCode as InventoryLevelValues | undefined;
    }
    if (inventoryLevel === InventoryLevels.physicalAvailable) {
        return productAvailability.PhysicalAvailableInventoryLevelCode as InventoryLevelValues | undefined;
    }
    return undefined;
};
